import React from 'react';
import { formatDurationClock } from '../../../utils/duration';
import './RecordingControl.css';

export type RecordingControlState = 'idle' | 'recording' | 'stopping' | 'disabled';

export interface RecordingControlProps {
  state: RecordingControlState;
  elapsedSeconds: number;
  minSeconds?: number;
  maxSeconds?: number;
  /** Input level 0..1 when the capture engine reports one. */
  level?: number;
  onStart: () => void;
  onStop: () => void;
  busy?: boolean;
  disabled?: boolean;
  /** Idle label override, for example "Record sample". */
  label?: string;
  className?: string;
}

/**
 * Presentational 64px record control. The circle becomes a stop square while
 * recording; the visible label and timer carry the meaning so the record
 * accent is never the only cue. Timers keep running under reduced motion.
 */
const RecordingControl: React.FC<RecordingControlProps> = ({
  state,
  elapsedSeconds,
  minSeconds,
  maxSeconds,
  level,
  onStart,
  onStop,
  busy = false,
  disabled = false,
  label,
  className,
}) => {
  const recording = state === 'recording';
  const stopping = state === 'stopping';
  const inactive = disabled || state === 'disabled' || stopping || busy;
  const visibleLabel = recording ? 'Stop recording' : stopping ? 'Stopping' : label ?? 'Record';
  const elapsed = formatDurationClock(elapsedSeconds);
  const showMinimum = recording && typeof minSeconds === 'number' && elapsedSeconds < minSeconds;
  const timerLabel = typeof maxSeconds === 'number' ? `${elapsed} of ${formatDurationClock(maxSeconds)}` : elapsed;
  const clampedLevel = typeof level === 'number' ? Math.min(1, Math.max(0, level)) : undefined;

  return (
    <div className={`luma-record luma-record--${state}${className ? ` ${className}` : ''}`} data-state={state}>
      <button
        type="button"
        className="luma-record__button"
        onClick={recording ? onStop : onStart}
        disabled={inactive}
        aria-busy={busy || undefined}
        aria-pressed={undefined}
      >
        <span className="luma-record__target" aria-hidden="true">
          <span className="luma-record__glyph" />
        </span>
        <span className="luma-record__label">{visibleLabel}</span>
      </button>
      <p className="luma-record__timer" role="timer" aria-live="off" aria-atomic="true">
        <span className="luma-sr-only">Recorded </span>
        {timerLabel}
      </p>
      {showMinimum && (
        <p className="luma-record__minimum">Keep going until {formatDurationClock(minSeconds)}</p>
      )}
      {typeof clampedLevel === 'number' && (
        <div
          className="luma-record__level"
          role="meter"
          aria-label="Microphone level"
          aria-valuemin={0}
          aria-valuemax={1}
          aria-valuenow={Number(clampedLevel.toFixed(2))}
          style={{ '--luma-record-level': clampedLevel } as React.CSSProperties}
        >
          <span className="luma-record__level-fill" />
        </div>
      )}
    </div>
  );
};

export default RecordingControl;
